import type { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { TimelineRoot } from '../timeline';
import type { TimelineStepData, TimelineStepDetails } from '../timeline';
import {
  Alert01Icon,
  ArrowDataTransferHorizontalIcon,
  BotIcon,
  SourceCodeIcon,
  HierarchyIcon,
  Shield01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import { Text } from '../../components/text';
import type { FindingTimelineDto } from '@terra/shared-contracts-findings';
import {
  AssetTypeSchema,
  type AssetTypeType,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import { Spinner } from '../../components/spinner';
import { Box } from '@terra/ui-styled-system';
import { canNavigateToDiscoveredAsset } from '../../utils/discovered-asset-navigation.utils';
import {
  FindingChainFlow,
  type FindingChainNodeSource,
} from './finding-chain-graph';
import {
  getFindingTimelineExecutionDisplayText,
  resolveExecutionOpenTitle,
} from '../../utils/findingTimelineExecutionDisplay';
import {
  getRetestVerdictBadge,
  getRetestVerdictBadgeTranslationKey,
} from '../../utils/findingRetestVerdictBadge';
import { buildSeverityChangeTimelineSteps } from './findingTimelineSeverityChangeSteps';

export type FindingTimelineProps = {
  findingTimeline: FindingTimelineDto | null;
  findingTargetType?: TargetTypeType;
  onViewTrace?: (
    {
      traceId,
      threadId,
      executionId,
      agentName,
    }: {
      traceId: string;
      threadId: string;
      executionId?: string | null;
      title?: string;
      agentName?: string | null;
    },
    event?: MouseEvent<HTMLElement>,
  ) => void;
  onViewAsset: (
    asset: {
      id: string;
      type: AssetTypeType;
      discoveredAssetType?: string | null;
    },
    event?: MouseEvent<HTMLElement>,
  ) => void;
  onViewSignal: (signalId: string, event?: MouseEvent<HTMLElement>) => void;
  onViewCodeChange: (url: string) => void;
  isLoading?: boolean;
  linkedFindings?: FindingChainNodeSource[];
  chainOrder?: string[];
  onNavigateToFinding?: (findingId: string) => void;
  isSeverityChangeRequestEnabled?: boolean;
};
export const FindingTimeline = ({
  findingTimeline,
  findingTargetType,
  onViewTrace,
  onViewAsset,
  onViewSignal,
  onViewCodeChange,
  isLoading = false,
  linkedFindings,
  chainOrder,
  onNavigateToFinding,
  isSeverityChangeRequestEnabled = false,
}: FindingTimelineProps) => {
  const t = useTranslations('designSystem.findings.timeline');
  const findingDrawerT = useTranslations('designSystem.findings.findingDrawer');
  const timelineSteps: TimelineStepData[] = useMemo(() => {
    if (!findingTimeline) {
      return [];
    }

    const firstAssetDiscoveryDate = findingTimeline.assets.reduce(
      (min, asset) => {
        return Math.min(min, new Date(asset.createdAt).getTime());
      },
      Infinity,
    );
    const steps: TimelineStepData[] = [];
    const retestLabel = findingDrawerT('retestButton');
    const viewRetestLabel = findingDrawerT('viewRetest');
    const resolvedRetestThreadId =
      findingTimeline.resolvedAfterRetest?.threadId ?? null;
    const createTraceDetail = ({
      execution,
      rightBadges,
      tooltip,
    }: {
      execution: NonNullable<FindingTimelineDto['execution']>;
      rightBadges: Array<{ label: string }>;
      tooltip: string;
    }): TimelineStepDetails => {
      const displayText = getFindingTimelineExecutionDisplayText({
        execution,
        executionLabel: t('trace'),
      });
      const handleTraceClick = (event?: MouseEvent<HTMLElement>) => {
        if (onViewTrace && execution.threadId) {
          onViewTrace(
            {
              traceId: execution.id,
              threadId: execution.threadId,
              executionId: execution.executionId ?? null,
              title: resolveExecutionOpenTitle({
                title: execution.title,
                agentName: execution.agentName,
              }),
              agentName: execution.agentName,
            },
            event,
          );
        }
      };

      const detailTitle = (
        <Box display="flex" alignItems="center" gap="1">
          <BotIcon size={14} />
          <Text>{displayText}</Text>
        </Box>
      );

      return {
        timestamp: execution.createdAt,
        title: detailTitle,
        onClick: handleTraceClick,
        onContextMenu: (event) => handleTraceClick(event),
        rightBadges,
        tooltip,
      };
    };

    if (findingTimeline.assets && findingTimeline.assets.length > 0) {
      steps.push({
        title: t('assetDiscovered'),
        icon: <Shield01Icon size={16} />,
        details: findingTimeline.assets.map((asset) => {
          const isEndpointAsset = asset.type === AssetTypeSchema.enum.ENDPOINT;
          const isDiscoveredAsset =
            asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET;
          const isNavigableDiscoveredAsset =
            isDiscoveredAsset &&
            canNavigateToDiscoveredAsset({
              assetType: asset.type,
              discoveredAssetType: asset.discoveredAssetType,
              targetType: findingTargetType,
            });
          const isClickableAsset =
            !isDiscoveredAsset || isNavigableDiscoveredAsset;
          const assetPath = isEndpointAsset
            ? asset.url || asset.path || ''
            : isDiscoveredAsset
              ? asset.port && asset.host
                ? `${asset.host}:${asset.port}`
                : asset.host || asset.id
              : asset.url || '';
          const operationName = isEndpointAsset
            ? (asset.soapOperationName ?? asset.graphqlOperationName ?? null)
            : null;
          const detailTitle =
            isEndpointAsset && operationName
              ? `${assetPath}?${operationName}`
              : assetPath;
          const endpointMethod = isEndpointAsset
            ? asset.method?.toUpperCase()
            : undefined;

          return {
            timestamp: asset.createdAt,
            title: detailTitle,
            ...(endpointMethod
              ? {
                  leftBadge: {
                    label: endpointMethod,
                  },
                }
              : {}),
            rightBadges: isEndpointAsset
              ? [
                  {
                    label:
                      asset.apiProtocol ??
                      (asset.soapOperationName
                        ? 'SOAP'
                        : asset.graphqlOperationName
                          ? 'GraphQL'
                          : 'REST'),
                  },
                  {
                    label: 'Endpoint',
                  },
                ]
              : isDiscoveredAsset
                ? [
                    {
                      label:
                        asset.discoveredAssetType ??
                        findingTargetType ??
                        asset.type,
                    },
                  ]
                : [
                    {
                      label: 'Webpage',
                    },
                  ],
            onClick: isClickableAsset ? () => onViewAsset(asset) : undefined,
            onContextMenu: isClickableAsset
              ? (event) => onViewAsset(asset, event)
              : undefined,
            tooltip: isClickableAsset
              ? t('tooltips.openAssetDiscoveryDrawer')
              : undefined,
          };
        }),
      });
    }

    if (findingTimeline.codeChange) {
      const codeChange = findingTimeline.codeChange;
      const codeChangeDate = new Date(codeChange.createdAt).getTime();
      const codeChangeUrl = codeChange.url;
      const codeChangeStepData: TimelineStepData = {
        title: t('codeChange'),
        icon: <SourceCodeIcon size={16} />,
        details: [
          {
            timestamp: codeChange.createdAt,
            title: `PR #${codeChange.prNumber}`,
            description: codeChange.prTitle || undefined,
            onClick: codeChangeUrl
              ? () => onViewCodeChange(codeChangeUrl)
              : undefined,
            tooltip: codeChangeUrl
              ? t('tooltips.openCodeChangeDrawer')
              : undefined,
            showLinkIcon: Boolean(codeChangeUrl),
          },
        ],
      };

      if (codeChangeDate < firstAssetDiscoveryDate) {
        steps.unshift(codeChangeStepData);
      } else {
        steps.push(codeChangeStepData);
      }
    }

    if (findingTimeline.signal) {
      const signal = findingTimeline.signal;

      steps.push({
        title: t('signalGenerated'),
        icon: <Target03Icon size={16} />,
        details: [
          {
            timestamp: signal.createdAt,
            title: signal.title,
            onClick: () => onViewSignal(signal.id),
            onContextMenu: (event) => onViewSignal(signal.id, event),
            tooltip: t('tooltips.openSignalDrawer'),
          },
        ],
      });
    }

    if (findingTimeline.execution) {
      const execution = findingTimeline.execution;
      const statusLabel =
        execution.status.charAt(0) + execution.status.slice(1).toLowerCase();

      steps.push({
        title: t('run'),
        icon: <BotIcon size={16} />,
        details: [
          createTraceDetail({
            execution,
            rightBadges: [{ label: t('trace') }, { label: statusLabel }],
            tooltip: t('tooltips.openRunSignalDrawer'),
          }),
        ],
      });
    }

    if (
      chainOrder &&
      chainOrder.length > 0 &&
      linkedFindings &&
      linkedFindings.length > 0
    ) {
      steps.push({
        id: 'chained-findings',
        title: t('chainedFindings'),
        icon: <HierarchyIcon size={16} />,
        content: (
          <FindingChainFlow
            findings={linkedFindings}
            orderedIds={chainOrder}
            onFindingClick={onNavigateToFinding}
          />
        ),
      });
    }

    steps.push({
      title: t('findingCreated'),
      icon: <Alert01Icon size={16} />,
    });

    const severityChangeEvents = isSeverityChangeRequestEnabled
      ? (findingTimeline.severityChangeEvents ?? [])
      : [];

    if (severityChangeEvents.length > 0) {
      steps.push(
        ...buildSeverityChangeTimelineSteps({
          events: severityChangeEvents,
          icon: <ArrowDataTransferHorizontalIcon size={16} />,
          t: (key, values) => (values ? t(key, values) : t(key)),
        }),
      );
    }

    const retestExecutions = findingTimeline.retestExecutions ?? [];

    if (retestExecutions.length > 0) {
      steps.push({
        title: retestLabel,
        icon: <BotIcon size={16} />,
        details: retestExecutions.map((execution) => {
          const verdict = getRetestVerdictBadge({
            status: execution.status,
            outcome: execution.outcome,
            resolvedForThisThread:
              Boolean(execution.threadId) &&
              execution.threadId === resolvedRetestThreadId,
          });

          return createTraceDetail({
            execution,
            rightBadges: [
              {
                label: t(getRetestVerdictBadgeTranslationKey(verdict)),
              },
            ],
            tooltip: viewRetestLabel,
          });
        }),
      });
    }

    if (findingTimeline.resolvedAfterRetest) {
      steps.push({
        title: t('findingResolved'),
        icon: <Shield01Icon size={16} />,
      });
    }

    return steps;
  }, [
    findingTimeline,
    findingTargetType,
    t,
    findingDrawerT,
    onViewTrace,
    onViewSignal,
    onViewAsset,
    onViewCodeChange,
    linkedFindings,
    chainOrder,
    onNavigateToFinding,
    isSeverityChangeRequestEnabled,
  ]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  return <TimelineRoot steps={timelineSteps} />;
};
